import api from './api';

// Edge Peering types based on API response
export interface EdgePeering {
  id: string;
  tenant_id: string;
  name: string;
  topology_type: 'hub-spoke' | 'mesh' | 'partial-mesh';
  hub_edge_id?: string;
  tunnel_subnet: string;
  listen_port: number;
  tunnel_protocol: 'wireguard' | 'ipsec';
  ipsec_crypto_alg?: string;
  ipsec_integ_alg?: string;
  ipsec_rekey_interval_hours?: number;
  bgp_enabled: boolean;
  bgp_mode: 'ibgp' | 'ebgp';
  base_asn: number;
  bfd_enabled: boolean;
  bfd_interval_ms: number;
  bfd_multiplier: number;
  dps_enabled: boolean;
  dps_probe_interval_ms: number;
  dps_max_latency_ms: number;
  dps_max_jitter_ms: number;
  dps_max_loss_pct: number;
  dps_switch_threshold: number;
  dps_recovery_threshold: number;
  redundancy_mode: 'single' | 'active-standby' | 'active-active';
  secondary_tunnel_subnet?: string;
  secondary_listen_port?: number;
  status: 'pending' | 'provisioning' | 'active' | 'degraded' | 'error';
  created_at: string;
  updated_at: string;
}

export interface EdgePeeringMember {
  id: string;
  peering_id: string;
  edge_id: string;
  member_type?: 'dedicated' | 'mtge' | 'connector';
  connector_id?: string;
  role: 'hub' | 'spoke' | 'mesh-peer';
  tunnel_ip: string;
  asn?: number;
  router_id?: string;
  wg_public_key?: string;
  wan_logical_port_id?: string;
  wan_port_name?: string;
  status: 'pending' | 'configured' | 'connected' | 'error';
  last_handshake?: string;
  created_at: string;
}

export interface EdgePeeringLink {
  id: string;
  peering_id: string;
  member_a_id: string;
  member_b_id: string;
  tunnel_index: number;
  endpoint_a?: string;
  endpoint_b?: string;
  status: 'pending' | 'up' | 'down' | 'error';
  bgp_session_state?: string;
  latency_ms?: number;
  jitter_ms?: number;
  packet_loss_pct?: number;
  dps_state?: string;
  dps_active_path?: number;
  dps_switched?: boolean;
  dps_switch_reason?: string;
  last_seen?: string;
}

export interface EdgePeeringRoute {
  id: string;
  member_id: string;
  prefix: string;
  route_type: 'lan' | 'static' | 'connected';
  advertise: boolean;
}

export interface WanInterface {
  id: string;
  logicalName: string;
  portName: string;
  ipv4: string;
  role: string;
}

export interface MemberTunnel {
  id: string;
  member_id: string;
  tunnel_index: number;
  wan_port_name?: string;
  tunnel_ip: string;
  listen_port: number;
  wg_public_key?: string;
  status: string;
  last_handshake_at?: string;
}

export interface PeeringStatus {
  peering: EdgePeering;
  members: Array<EdgePeeringMember & { edge_name?: string }>;
  links: Array<EdgePeeringLink & { member_a_name?: string; member_b_name?: string }>;
  health: {
    total_members: number;
    connected_members: number;
    total_links: number;
    links_up: number;
    bgp_sessions_established: number;
  };
  dpsStats?: {
    enabled: boolean;
    healthy: number;
    degraded: number;
    switched: number;
  };
}

export interface TopologyGraph {
  nodes: Array<{
    id: string;
    label: string;
    type: 'hub' | 'spoke' | 'mesh-peer';
    status: string;
    tunnel_ip?: string;
    routes_learned?: number;
    routes_advertised?: number;
  }>;
  edges: Array<{
    source: string;
    target: string;
    status: string;
    latency_ms?: number;
    bgp_state?: string;
  }>;
}

export interface CreatePeeringData {
  name: string;
  topology_type: 'hub-spoke' | 'mesh' | 'partial-mesh';
  hub_edge_id?: string;
  tunnel_subnet: string;
  listen_port?: number;
  bgp_enabled?: boolean;
  bgp_mode?: 'ibgp' | 'ebgp';
  base_asn?: number;
  bfd_enabled?: boolean;
  bfd_interval_ms?: number;
  bfd_multiplier?: number;
  dpsEnabled?: boolean;
  dpsProbeIntervalMs?: number;
  dpsMaxLatencyMs?: number;
  dpsMaxJitterMs?: number;
  dpsMaxLossPct?: number;
  dpsSwitchThreshold?: number;
  dpsRecoveryThreshold?: number;
  redundancyMode?: 'single' | 'active-standby' | 'active-active';
  secondaryTunnelSubnet?: string;
  secondaryListenPort?: number;
  tunnelProtocol?: 'wireguard' | 'ipsec';
  ipsecCryptoAlg?: string;
  ipsecRekeyIntervalHours?: number;
}

export interface AddMemberData {
  edgeId?: string;
  mtgeId?: string;
  connectorId?: string;
  role: 'hub' | 'spoke' | 'mesh-peer';
  primaryWanPortId?: string;
  secondaryWanPortId?: string;
  peerWith?: string[];
}

export interface AddRouteData {
  prefix: string;
  route_type: 'lan' | 'static' | 'connected';
  origin?: string;
  advertise?: boolean;
}

export interface PreflightCheck {
  member: string;
  level: 'ok' | 'warning' | 'error';
  message: string;
}

export interface SyncMemberResult {
  member: string;
  success: boolean;
  error?: string;
}

export interface SyncResult {
  preflight: {
    checks: PreflightCheck[];
    canSync: boolean;
  };
  sync: {
    attempted: boolean;
    results: SyncMemberResult[];
  } | null;
}

// Response type from V2 API
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  timestamp: string;
}

interface PaginatedResponse<T> {
  items: T[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

const edgePeeringService = {
  /**
   * List all peering topologies
   */
  listPeerings: async (): Promise<{ items: EdgePeering[]; pagination: any }> => {
    const response = await api.get('/api/v2/peering') as ApiResponse<PaginatedResponse<EdgePeering>>;
    return response.data;
  },

  /**
   * Get peering by ID
   */
  getPeering: async (id: string): Promise<EdgePeering> => {
    const response = await api.get(`/api/v2/peering/${id}`) as ApiResponse<EdgePeering>;
    return response.data;
  },

  /**
   * Create new peering topology
   */
  createPeering: async (data: CreatePeeringData): Promise<EdgePeering> => {
    const response = await api.post('/api/v2/peering', data) as ApiResponse<EdgePeering>;
    return response.data;
  },

  /**
   * Update peering configuration
   */
  updatePeering: async (id: string, data: Partial<CreatePeeringData>): Promise<EdgePeering> => {
    const response = await api.put(`/api/v2/peering/${id}`, data) as ApiResponse<EdgePeering>;
    return response.data;
  },

  /**
   * Delete peering topology
   */
  deletePeering: async (id: string): Promise<void> => {
    await api.delete(`/api/v2/peering/${id}`);
  },

  /**
   * Add member to peering
   */
  addMember: async (peeringId: string, data: AddMemberData): Promise<EdgePeeringMember> => {
    const response = await api.post(`/api/v2/peering/${peeringId}/members`, data) as ApiResponse<EdgePeeringMember>;
    return response.data;
  },

  /**
   * Remove member from peering
   */
  removeMember: async (peeringId: string, edgeId: string): Promise<void> => {
    await api.delete(`/api/v2/peering/${peeringId}/members/${edgeId}`);
  },

  /**
   * Pre-flight validation + sync peering config to all edges
   */
  syncPeering: async (id: string): Promise<SyncResult> => {
    const response = await api.post(`/api/v2/peering/${id}/sync`) as ApiResponse<SyncResult>;
    return response.data;
  },

  /**
   * Get real-time peering status
   */
  getPeeringStatus: async (id: string): Promise<PeeringStatus> => {
    const response = await api.get(`/api/v2/peering/${id}/status`) as ApiResponse<PeeringStatus>;
    return response.data;
  },

  /**
   * Get routes for a peering topology
   */
  getRoutes: async (peeringId: string): Promise<any> => {
    const response = await api.get(`/api/v2/peering/${peeringId}/routes`) as ApiResponse<any>;
    return response.data;
  },

  /**
   * Add route to member
   */
  addRoute: async (peeringId: string, memberId: string, data: AddRouteData): Promise<EdgePeeringRoute> => {
    const response = await api.post(`/api/v2/peering/${peeringId}/members/${memberId}/routes`, data) as ApiResponse<EdgePeeringRoute>;
    return response.data;
  },

  /**
   * Update route advertisement
   */
  updateRoute: async (peeringId: string, routeId: string, advertise: boolean): Promise<EdgePeeringRoute> => {
    const response = await api.patch(`/api/v2/peering/${peeringId}/routes/${routeId}`, { advertise }) as ApiResponse<EdgePeeringRoute>;
    return response.data;
  },

  /**
   * Delete route
   */
  deleteRoute: async (peeringId: string, routeId: string): Promise<void> => {
    await api.delete(`/api/v2/peering/${peeringId}/routes/${routeId}`);
  },

  /**
   * Get topology graph data for visualization
   */
  getTopologyGraph: async (): Promise<TopologyGraph> => {
    const response = await api.get('/api/v2/topology/graph') as ApiResponse<TopologyGraph>;
    return response.data;
  },

  /**
   * Get WAN interfaces for an edge device
   */
  getWanInterfaces: async (edgeId: string): Promise<WanInterface[]> => {
    const response = await api.get(`/api/v2/peering/edges/${edgeId}/wan-interfaces`) as ApiResponse<WanInterface[]>;
    return response.data;
  },

  /**
   * Add a link between two members (partial-mesh)
   */
  addLink: async (peeringId: string, memberAId: string, memberBId: string): Promise<EdgePeeringLink[]> => {
    const response = await api.post(`/api/v2/peering/${peeringId}/links`, { memberAId, memberBId }) as ApiResponse<EdgePeeringLink[]>;
    return response.data;
  },

  /**
   * Remove a link between two members (partial-mesh)
   */
  removeLink: async (peeringId: string, linkId: string): Promise<void> => {
    await api.delete(`/api/v2/peering/${peeringId}/links/${linkId}`);
  },

  /**
   * Rekey IPSec SA keys for a peering
   */
  rekeyIPSec: async (peeringId: string): Promise<{ linksRekeyed: number }> => {
    const response = await api.post(`/api/v2/peering/${peeringId}/rekey`) as ApiResponse<{ linksRekeyed: number }>;
    return response.data;
  },
};

export default edgePeeringService;
